import { ref, computed, readonly, type Ref } from "vue";
import { useConvexClient } from "convex-vue";
import { createEventHook } from "@vueuse/core";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { getCurrentUser } from "./useSupabaseAuth";
import type {
  Room,
  RoomParticipant,
  ParticipantScore,
  TimeSyncState,
  ConnectionStatus,
  CreateRoomInput,
  JoinRoomInput,
  ClassroomSong,
  LoopSource,
} from "@/types/classroom";

// Number of ping/pong samples for NTP-style sync
const TIME_SYNC_SAMPLES = 8;
const TIME_SYNC_MAX_AGE_MS = 15000;
// How long the held sample defends its RTT before a worse one may replace it.
const TIME_SYNC_BEST_WINDOW_MS = 30000;
// Offset move worth logging, so a correction is visible without per-sample spam.
const TIME_SYNC_LOG_THRESHOLD_MS = 100;

// ── Singleton state ───────────────────────────────────────────────────────────

const room: Ref<Room | null> = ref(null);
const participants: Ref<RoomParticipant[]> = ref([]);
const scores: Ref<ParticipantScore[]> = ref([]);
const selectedSong: Ref<ClassroomSong | null> = ref(null);
const connectionStatus: Ref<ConnectionStatus> = ref("disconnected");
const error: Ref<string | null> = ref(null);

const timeSync: Ref<TimeSyncState> = ref({
  serverOffset: 0,
  lastSyncAt: 0,
  roundTripTime: 0,
  isSynced: false,
});

// Watch subscriptions (unsubscribe functions)
let unsubRoom: (() => void) | null = null;
let unsubParticipants: (() => void) | null = null;
let unsubScores: (() => void) | null = null;

// Heartbeat interval
let _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
const HEARTBEAT_INTERVAL = 5000; // ms

// Convex client — stored on first use (always called from setup context)
let _client: ReturnType<typeof useConvexClient> | null = null;
let _syncTimeInFlight: Promise<void> | null = null;

// Track previous room state to detect transitions
let prevRoomStatus: string | null = null;
let prevMusicalPieceId: string | null | undefined = null;
let prevScheduledStartTime: number | undefined = undefined;
let prevLoopStartMeasure: number | null | undefined = undefined;
let prevLoopEndMeasure: number | null | undefined = undefined;
let prevLoopSource: LoopSource = undefined;
let prevCurrentTimeMs: number | null = null;
let latestMusicalPieceLoadRequestId = 0;

// Module-level cached userId
let _cachedUserId: string | null = null;

// ── Event hooks ───────────────────────────────────────────────────────────────

const sessionStartHook = createEventHook<void>();
const playbackStartHook = createEventHook<{ scheduledTime: number; songTimeMs: number }>();
const playbackPauseHook = createEventHook<{ pausedAtMs: number }>();
const playbackResumeHook = createEventHook<{ scheduledTime: number; resumeAtMs: number }>();
const playbackStopHook = createEventHook<void>();
const scoreUpdateHook = createEventHook<{ scores: ParticipantScore[] }>();
const gameEndHook = createEventHook<void>();
const loopSectionUpdateHook = createEventHook<{
  startMeasure: number | null;
  endMeasure: number | null;
  source: LoopSource;
}>();

export function useMultiplayerRoomConvex() {
  // Obtain (and cache) the Convex client on first setup-context call
  if (!_client) {
    _client = useConvexClient();
  }
  const client = _client;

  // ── Computed ───────────────────────────────────────────────────────────────

  const isHost = computed(() => {
    if (!room.value || !_cachedUserId) return false;
    return room.value.hostId === _cachedUserId;
  });

  const isConnected = computed(() => connectionStatus.value === "connected");
  const roomCode = computed(() => room.value?.code ?? "");

  const currentParticipant = computed(() => {
    if (!_cachedUserId) return null;
    return participants.value.find((p) => p.userId === _cachedUserId) ?? null;
  });

  const sortedScores = computed(() =>
    [...scores.value]
      .sort((a, b) => b.score - a.score)
      .map((s, i) => ({ ...s, rank: i + 1 }))
  );

  // ── Time sync ──────────────────────────────────────────────────────────────

  /** Returns current server time (epoch ms) adjusted for clock offset */
  const getWorldTime = (): number => Date.now() + timeSync.value.serverOffset;

  /** Converts a server epoch timestamp to a local Date.now()-equivalent */
  const worldToLocalTime = (worldTime: number): number =>
    worldTime - timeSync.value.serverOffset;

  /**
   * Offers one round trip as a clock-offset estimate, keeping it only if it beats
   * the held one.
   *
   * `offset = serverTime - t1 - rtt/2` assumes symmetric latency, so a sample is
   * wrong by up to rtt/2 when it isn't — and a main-thread stall (score render,
   * GC) or a queued mutation response inflates RTT into the seconds, which is how
   * a client ends up believing the server clock is seconds from where it is.
   * Lowest RTT wins because it carries the tightest error bound; averaging or
   * taking a median only blends the good samples with the bad.
   *
   * The held sample defends its RTT for TIME_SYNC_BEST_WINDOW_MS only. Drift is
   * not the reason (quartz drift is ~1ms/minute) — clock *steps* are: an iOS NTP
   * correction or a wake from sleep invalidates the offset outright, and without
   * expiry one lucky sample would pin a stale offset for the whole session. On
   * expiry a worse-RTT sample may take over; later ones in the window beat it.
   */
  const offerClockSample = (t1: number, serverTime: number, t2: number): void => {
    const now = Date.now();
    const rtt = t2 - t1;
    const previous = timeSync.value;
    const windowExpired = now - previous.lastSyncAt > TIME_SYNC_BEST_WINDOW_MS;
    if (previous.isSynced && !windowExpired && rtt >= previous.roundTripTime) {
      return;
    }

    const serverOffset = serverTime - t1 - rtt / 2;
    // The client ships over OTA independently of the Convex deployment, so a new
    // build can briefly meet a backend whose heartbeat returns no timestamp. A
    // non-finite offset would poison getWorldTime() for every consumer.
    if (!Number.isFinite(serverOffset)) {
      return;
    }

    timeSync.value = {
      serverOffset,
      lastSyncAt: now,
      roundTripTime: rtt,
      isSynced: true,
    };

    if (
      !previous.isSynced ||
      Math.abs(serverOffset - previous.serverOffset) > TIME_SYNC_LOG_THRESHOLD_MS
    ) {
      console.log(
        `[TimeSync] offset=${serverOffset.toFixed(0)}ms  RTT=${rtt}ms`
      );
    }
  };

  /**
   * Blocking NTP-style sync, for the points that need an offset before they can
   * proceed. Heartbeats keep it fresh from there (see startHeartbeat), so this
   * no-ops whenever the held sample is still recent.
   */
  const syncTime = async (): Promise<void> => {
    const isFresh =
      timeSync.value.isSynced &&
      Date.now() - timeSync.value.lastSyncAt <= TIME_SYNC_MAX_AGE_MS;

    if (isFresh) {
      return;
    }

    if (_syncTimeInFlight) {
      return _syncTimeInFlight;
    }

    _syncTimeInFlight = (async () => {
      for (let i = 0; i < TIME_SYNC_SAMPLES; i++) {
        const t1 = Date.now();
        const serverTime: number = await client.mutation(
          api.rooms.getServerTime,
          {}
        );
        offerClockSample(t1, serverTime, Date.now());

        if (i < TIME_SYNC_SAMPLES - 1) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }
    })();

    try {
      await _syncTimeInFlight;
    } finally {
      _syncTimeInFlight = null;
    }
  };

  // ── Internal helpers ───────────────────────────────────────────────────────

  /** Handle room state transitions (replaces Supabase broadcast events) */
  const handleRoomTransition = (newRoom: Room | null) => {
    if (!newRoom) return;

    const newStatus = newRoom.status;
    const newMusicalPieceId = newRoom.musicalPieceId as string | undefined;

    // Musical piece selected
    if (newMusicalPieceId && newMusicalPieceId !== prevMusicalPieceId && newRoom.musicalPieceId) {
      loadSelectedMusicalPiece(newRoom.musicalPieceId);
    }

    // Session / playback transitions
    const newScheduledTime = newRoom.scheduledStartTime;

    // Handle "playing" transitions separately — including restart while already playing
    // (playing → playing with a new scheduledStartTime means broadcastPlay was called again)
    if (newStatus === "playing" && newScheduledTime !== undefined &&
        newScheduledTime !== prevScheduledStartTime) {
      if (prevRoomStatus === "paused") {
        playbackResumeHook.trigger({
          scheduledTime: newScheduledTime,
          resumeAtMs: newRoom.currentTimeMs,
        });
      } else {
        playbackStartHook.trigger({
          scheduledTime: newScheduledTime,
          songTimeMs: newRoom.currentTimeMs,
        });
      }
    } else if (prevRoomStatus !== null && prevRoomStatus !== newStatus) {
      if (newStatus === "ready") {
        sessionStartHook.trigger();
      } else if (newStatus === "paused") {
        playbackPauseHook.trigger({
          pausedAtMs: newRoom.currentTimeMs,
        });
      } else if (newStatus === "ended") {
        gameEndHook.trigger();
      } else if (
        newStatus === "waiting" &&
        (prevRoomStatus === "playing" || prevRoomStatus === "ended")
      ) {
        playbackStopHook.trigger();
      }
    }

    // While remaining paused, allow host seek drags to stream pause-time updates.
    if (
      prevRoomStatus === "paused" &&
      newStatus === "paused" &&
      prevCurrentTimeMs !== null &&
      Math.abs(newRoom.currentTimeMs - prevCurrentTimeMs) >= 4
    ) {
      playbackPauseHook.trigger({
        pausedAtMs: newRoom.currentTimeMs,
      });
    }

    // Loop section changes (host controls, all players follow)
    const loopChanged =
      prevLoopStartMeasure !== newRoom.loopStartMeasure ||
      prevLoopEndMeasure !== newRoom.loopEndMeasure ||
      prevLoopSource !== newRoom.loopSource;
      
    if (loopChanged) {
      loopSectionUpdateHook.trigger({
        startMeasure: newRoom.loopStartMeasure ?? null,
        endMeasure: newRoom.loopEndMeasure ?? null,
        source: newRoom.loopSource,
      });
    }

    prevRoomStatus = newStatus;
    prevMusicalPieceId = newMusicalPieceId;
    prevScheduledStartTime = newScheduledTime;
    prevLoopStartMeasure = newRoom.loopStartMeasure;
    prevLoopEndMeasure = newRoom.loopEndMeasure;
    prevLoopSource = newRoom.loopSource;
    prevCurrentTimeMs = newRoom.currentTimeMs;
  };

  const loadSelectedMusicalPiece = async (pieceId: Id<"musicalPieces">) => {
    const requestId = ++latestMusicalPieceLoadRequestId;
    try {
      const files = await client.query(api.musicalPieces.getFiles, { id: pieceId });
      const piece = await client.query(api.musicalPieces.getById, { id: pieceId });
      // Ignore stale responses when host switches pieces quickly.
      if (requestId !== latestMusicalPieceLoadRequestId) return;
      if (room.value?.musicalPieceId !== pieceId) return;
      if (!files) return;
      selectedSong.value = {
        musicalPieceId: pieceId,
        name: room.value?.songName ?? "Unknown",
        tempo: files.tempo ?? undefined,
        sections: piece?.sections,
        sheetMusicUrl: files.sheetMusicUrl!,
        sheetMusicFilename: files.sheetMusicFilename,
        audioUrl: files.audioUrl ?? undefined,
        audioFilename: files.audioFilename ?? undefined,
        audioOffsetMs: files.audioOffsetMs,
      };
    } catch (err) {
      console.error("[Room] Failed to load musical piece files:", err);
    }
  };

  const syncSelectedSongFromRoom = (nextRoom: Room | null) => {
    if (!nextRoom?.musicalPieceId) {
      selectedSong.value = null;
      return;
    }

    const alreadyHydrated =
      selectedSong.value?.musicalPieceId === nextRoom.musicalPieceId;

    if (!alreadyHydrated) {
      loadSelectedMusicalPiece(nextRoom.musicalPieceId);
    }
  };

  const subscribeToRoom = (roomId: Id<"rooms">, code: string) => {
    unsubRoom?.();
    const subRoom = client.onUpdate(api.rooms.getByCode, { code }, (newRoom) => {
      const r = (newRoom as Room | null) ?? null;
      if (!r) {
        room.value = null;
        participants.value = [];
        scores.value = [];
        selectedSong.value = null;
        error.value = "Room not found";
        teardown();
        return;
      }
      handleRoomTransition(r);
      room.value = r;
      syncSelectedSongFromRoom(r);
    });
    unsubRoom = () => subRoom();

    unsubParticipants?.();
    const subPart = client.onUpdate(api.rooms.getParticipants, { roomId }, (newParts) => {
      participants.value = (newParts as RoomParticipant[]) ?? [];
    });
    unsubParticipants = () => subPart();
  };

  const subscribeToScores = (roomId: Id<"rooms">) => {
    unsubScores?.();
    const subScores = client.onUpdate(api.rooms.getScores, { roomId }, (newScores) => {
      const updated = (newScores as ParticipantScore[]) ?? [];
      scores.value = updated;
      scoreUpdateHook.trigger({ scores: updated });
    });
    unsubScores = () => subScores();
  };

  const stopHeartbeat = () => {
    if (_heartbeatTimer) {
      clearInterval(_heartbeatTimer);
      _heartbeatTimer = null;
    }
  };

  const startHeartbeat = (roomId: Id<"rooms">) => {
    stopHeartbeat();
    // A backgrounded webview runs no timers, so a client that has been asleep
    // long enough comes back with no participant row. That needs no handling
    // here: the interval resumes with the webview, and the next beat re-admits
    // it (see the upsert in convex/rooms.ts heartbeat).
    _heartbeatTimer = setInterval(async () => {
      try {
        const t1 = Date.now();
        const serverTime = await client.mutation(api.rooms.heartbeat, { roomId });
        offerClockSample(t1, serverTime, Date.now());
      } catch (err) {
        console.warn("[Room] Heartbeat failed:", err);
      }
    }, HEARTBEAT_INTERVAL);
  };

  const teardown = () => {
    unsubRoom?.();
    unsubParticipants?.();
    unsubScores?.();
    unsubRoom = null;
    unsubParticipants = null;
    unsubScores = null;
    stopHeartbeat();
    // The room snapshot is left as-is (see leaveRoom), so this is what tells a
    // view re-entering the classroom that the state it can see is no longer
    // backed by a live subscription and has to be re-established.
    connectionStatus.value = "disconnected";
  };

  // ── Public API ─────────────────────────────────────────────────────────────

  const createRoom = async (input?: CreateRoomInput): Promise<string> => {
    const currentUser = await getCurrentUser();
    if (!currentUser) throw new Error("Must be logged in to create a room");

    error.value = null;
    connectionStatus.value = "connecting";
    _cachedUserId = currentUser.id;

    try {
      await syncTime();
      const result: { roomId: Id<"rooms">; code: string } =
        await client.mutation(api.rooms.create, { name: input?.name });

      const created = await client.query(api.rooms.getByCode, {
        code: result.code,
      });
      room.value = created as Room;
      prevRoomStatus = "waiting";
      prevMusicalPieceId = undefined;
      prevScheduledStartTime = undefined;
      prevLoopStartMeasure = created?.loopStartMeasure;
      prevLoopEndMeasure = created?.loopEndMeasure;
      prevLoopSource = created?.loopSource;
      prevCurrentTimeMs = created?.currentTimeMs ?? 0;

      subscribeToRoom(result.roomId, result.code);
      subscribeToScores(result.roomId);
      startHeartbeat(result.roomId);
      connectionStatus.value = "connected";
      return result.code;
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to create room";
      connectionStatus.value = "error";
      throw err;
    }
  };

  const joinRoom = async (input: JoinRoomInput): Promise<boolean> => {
    const currentUser = await getCurrentUser();
    if (!currentUser) throw new Error("Must be logged in to join a room");

    error.value = null;
    connectionStatus.value = "connecting";
    _cachedUserId = currentUser.id;

    try {
      await syncTime();
      const result: { roomId: Id<"rooms">; code: string } =
        await client.mutation(api.rooms.join, {
          code: input.code,
          displayName: input.displayName,
        });

      const joined = await client.query(api.rooms.getByCode, {
        code: result.code,
      });
      room.value = joined;
      if (joined?.musicalPieceId) {
        await loadSelectedMusicalPiece(joined.musicalPieceId);
      } else {
        selectedSong.value = null;
      }
      prevRoomStatus = joined?.status ?? "waiting";
      prevMusicalPieceId = joined?.musicalPieceId;
      prevScheduledStartTime = joined?.scheduledStartTime;
      prevLoopStartMeasure = joined?.loopStartMeasure;
      prevLoopEndMeasure = joined?.loopEndMeasure;
      prevLoopSource = joined?.loopSource;
      prevCurrentTimeMs = joined?.currentTimeMs ?? 0;

      subscribeToRoom(result.roomId, result.code);
      subscribeToScores(result.roomId);
      startHeartbeat(result.roomId);
      connectionStatus.value = "connected";
      return true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to join room";
      connectionStatus.value = "error";
      throw err;
    }
  };

  const reconnectToRoom = async (code: string): Promise<boolean> => {
    const currentUser = await getCurrentUser();
    if (!currentUser) throw new Error("Must be logged in to reconnect to a room");

    error.value = null;
    connectionStatus.value = "connecting";
    _cachedUserId = currentUser.id;

    try {
      await syncTime();
      const normalizedCode = code.toUpperCase();
      const existingRoom = await client.query(api.rooms.getByCode, {
        code: normalizedCode,
      });
      if (!existingRoom) {
        throw new Error("Room not found");
      }

      room.value = existingRoom;
      if (existingRoom.musicalPieceId) {
        await loadSelectedMusicalPiece(existingRoom.musicalPieceId);
      } else {
        selectedSong.value = null;
      }

      prevRoomStatus = existingRoom.status ?? "waiting";
      prevMusicalPieceId = existingRoom.musicalPieceId;
      prevScheduledStartTime = existingRoom.scheduledStartTime;
      prevLoopStartMeasure = existingRoom.loopStartMeasure;
      prevLoopEndMeasure = existingRoom.loopEndMeasure;
      prevLoopSource = existingRoom.loopSource;
      prevCurrentTimeMs = existingRoom.currentTimeMs ?? 0;

      const existingParticipants = await client.query(api.rooms.getParticipants, {
        roomId: existingRoom._id,
      });
      participants.value = (existingParticipants) ?? [];
      const isParticipant = participants.value.some((participant) => participant.userId === currentUser.id);

      if (!isParticipant) {
        const fallbackDisplayName =
          (currentUser.user_metadata?.displayName as string | undefined) ??
          (currentUser.user_metadata?.full_name as string | undefined) ??
          (currentUser.user_metadata?.name as string | undefined) ??
          currentUser.email?.split("@")[0] ??
          "Student";
        await client.mutation(api.rooms.join, {
          code: normalizedCode,
          displayName: fallbackDisplayName,
        });
      }

      subscribeToRoom(existingRoom._id, existingRoom.code);
      subscribeToScores(existingRoom._id);
      startHeartbeat(existingRoom._id);
      connectionStatus.value = "connected";
      return true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to reconnect to room";
      connectionStatus.value = "error";
      throw err;
    }
  };

  /**
   * Leaves the room: stops this client's heartbeat, then gives up its seat.
   *
   * Stopping the heartbeat is the half that matters. Presence expires on its
   * own, so a client that stops beating is out within one TTL whether or not
   * the mutation lands — while a client that walks out of the classroom with
   * its timer still running stays in the room forever, which no server-side
   * sweep can fix. The mutation only makes the departure immediate.
   *
   * Room state is deliberately left populated. This runs from a route guard,
   * with the classroom views still mounted and watching `room` for the
   * host-closed-the-room redirect — clearing it here would fire that redirect
   * and hijack the destination the user is navigating to. `connectionStatus`
   * going to "disconnected" is what marks the snapshot as no longer live; the
   * next createRoom/joinRoom/reconnectToRoom replaces it wholesale.
   */
  const leaveRoom = async (): Promise<void> => {
    const roomId = room.value?._id;
    // A beat already on the wire can still land after the leave and push the
    // expiry back out. That costs one TTL of staleness on the teacher's list
    // and then corrects itself, which is not worth a barrier to prevent.
    teardown();
    if (!roomId) return;
    try {
      await client.mutation(api.rooms.leave, { roomId });
    } catch (err) {
      console.warn("[Room] Failed to leave room:", err);
    }
  };

  const selectMusicalPiece = async (
    musicalPieceId: Id<"musicalPieces">,
    songName: string
  ): Promise<void> => {
    if (!room.value) return;
    await client.mutation(api.rooms.selectMusicalPiece, {
      roomId: room.value._id,
      musicalPieceId,
      songName,
    });
  };

  const startSession = async (): Promise<void> => {
    if (!room.value) return;
    await client.mutation(api.rooms.startSession, { roomId: room.value._id });
  };

  const markSheetMusicLoaded = async (): Promise<void> => {
    if (!room.value) return;
    await client.mutation(api.rooms.markSheetMusicLoaded, {
      roomId: room.value._id,
    });
  };

  const broadcastPlay = async (currentTimeMs: number = 0): Promise<void> => {
    if (!room.value) return;
    await syncTime();
    await client.mutation(api.rooms.broadcastPlay, {
      roomId: room.value._id,
      currentTimeMs,
    });
  };

  const broadcastPause = async (pausedAtMs: number): Promise<void> => {
    if (!room.value) return;
    await client.mutation(api.rooms.broadcastPause, {
      roomId: room.value._id,
      pausedAtMs,
    });
  };

  const broadcastResume = async (resumeAtMs: number): Promise<void> => {
    if (!room.value) return;
    await syncTime();
    await client.mutation(api.rooms.broadcastResume, {
      roomId: room.value._id,
      resumeAtMs,
    });
  };

  const returnToLobby = async (): Promise<void> => {
    if (!room.value) return;
    await client.mutation(api.rooms.broadcastReturnToLobby, { roomId: room.value._id });
  };

  /**
   * Closes the room for everyone. The host's counterpart to leaveRoom, and it
   * stops this client's traffic for the same reason and in the same order:
   * teardown first, so a mutation that fails can't leave this client beating
   * into a room it has walked out of.
   */
  const closeRoom = async (): Promise<void> => {
    const roomId = room.value?._id;
    teardown();
    if (!roomId) return;
    await client.mutation(api.rooms.closeRoom, { roomId });
  };

  const broadcastGameEnd = async (): Promise<void> => {
    if (!room.value) return;
    await client.mutation(api.rooms.broadcastGameEnd, {
      roomId: room.value._id,
    });
  };
  // ── Loop section management (host only) ──────────────────────────────────────

  const updateLoopSection = async (
    loopStartMeasure: number | null,
    loopEndMeasure: number | null,
    loopSource: LoopSource
  ): Promise<void> => {
    if (!room.value) return;
    await client.mutation(api.rooms.updateLoopSection, {
      roomId: room.value._id,
      loopStartMeasure: loopStartMeasure ?? undefined,
      loopEndMeasure: loopEndMeasure ?? undefined,
      loopSource,
    });
  };

  // ── Loop lead-in mode management (host only) ─────────────────────────────────
  const updateLoopPreRollMode = async (everyPass: boolean): Promise<void> => {
    if (!room.value) return;
    await client.mutation(api.rooms.updateLoopPreRollMode, {
      roomId: room.value._id,
      everyPass,
    });
  };

  // ── Preview mode management (host only) ──────────────────────────────────────
  const updatePreviewMode = async (enabled: boolean): Promise<void> => {
    if (!room.value) return;
    await client.mutation(api.rooms.updatePreviewMode, {
      roomId: room.value._id,
      enabled,
    });
  };

  // ── Playback speed management (host only) ────────────────────────────────────
  const updatePlaybackSpeed = async (playbackSpeed: number): Promise<void> => {
    if (!room.value) return;
    await client.mutation(api.rooms.updatePlaybackSpeed, {
      roomId: room.value._id,
      playbackSpeed,
    });
  };

  // ── Score management ──────────────────────────────────────────────────────────
  const resetScores = async (): Promise<void> => {
    if (!room.value) return;
    await client.mutation(api.rooms.resetScores, { roomId: room.value._id });
  };

  const broadcastScoreUpdate = async (scoreData: {
    score: number;
    accuracy: number;
    comboCount: number;
    perfectCount: number;
    goodCount: number;
    missedCount: number;
    totalNotes: number;
  }): Promise<void> => {
    if (!room.value || !currentParticipant.value) return;
    await client.mutation(api.rooms.updateScore, {
      roomId: room.value._id,
      displayName: currentParticipant.value.displayName,
      ...scoreData,
    });
  };

  const submitScore = async (scoreData: {
    score: number;
    accuracy: number;
    perfectCount: number;
    goodCount: number;
    missedCount: number;
    longestCombo: number;
    totalNotes: number;
  }): Promise<void> => {
    if (!room.value || !currentParticipant.value) return;
    await client.mutation(api.rooms.submitScore, {
      roomId: room.value._id,
      displayName: currentParticipant.value.displayName,
      ...scoreData,
    });
  };

  return {
    // State (readonly)
    room: readonly(room),
    participants: readonly(participants),
    scores: readonly(scores),
    selectedSong: readonly(selectedSong),
    connectionStatus: readonly(connectionStatus),
    error: readonly(error),
    timeSync: readonly(timeSync),

    // Computed
    isHost,
    isConnected,
    roomCode,
    currentParticipant,
    sortedScores,

    // Time sync
    syncTime,
    getWorldTime,
    worldToLocalTime,

    // Room lifecycle
    createRoom,
    joinRoom,
    reconnectToRoom,
    leaveRoom,

    // Piece management
    selectMusicalPiece,
    
    // Loop section management (host only)
    updateLoopSection,

    // Loop lead-in mode management (host only)
    updateLoopPreRollMode,

    // Preview mode management (host only)
    updatePreviewMode,

    // Playback speed management (host only)
    updatePlaybackSpeed,

    // Session control (host only)
    startSession,
    markSheetMusicLoaded,

    // Playback control (host only)
    broadcastPlay,
    broadcastPause,
    broadcastResume,
    returnToLobby,
    closeRoom,
    broadcastGameEnd,

    // Score management
    resetScores,
    broadcastScoreUpdate,
    submitScore,

    // Event subscriptions
    onSessionStart: sessionStartHook.on,
    onPlaybackStart: playbackStartHook.on,
    onPlaybackPause: playbackPauseHook.on,
    onPlaybackResume: playbackResumeHook.on,
    onReturnToLobby: playbackStopHook.on,
    onScoreUpdate: scoreUpdateHook.on,
    onGameEnd: gameEndHook.on,
    onLoopSectionUpdate: loopSectionUpdateHook.on,
  };
}
